import { Globe, Heart, Menu, User } from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { CustomSelect } from '../components/custom-select.js';

export function SiteLayout(): React.JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border bg-background shadow-card">
        <div className="mx-auto flex max-w-[1536px] items-center justify-between px-6 lg:px-8 py-3">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 text-foreground">
              <img alt="CLLIX" className="h-8 w-8 object-contain" src="/cllix-logo.png" />
              <span className="text-xl font-extrabold tracking-tight">CLLIX</span>
              <span className="hidden text-sm font-semibold text-foreground sm:inline">
                Apartments and Hotels
              </span>
            </Link>
            <nav className="hidden items-center gap-6 md:flex">
              <HeaderNavLink to="/">Stays</HeaderNavLink>
              <HeaderNavLink to="/search">Apartments</HeaderNavLink>
              <HeaderLink to="/">Offers</HeaderLink>
              <HeaderLink to="/">Destinations</HeaderLink>
              <HeaderLink to="/">About CLLIX</HeaderLink>
            </nav>
          </div>

          <div className="hidden items-center gap-3 sm:flex">
            <CustomSelect
              value="AUD"
              onChange={() => undefined}
              options={[
                { value: 'AUD', label: 'AUD ($)' },
                { value: 'USD', label: 'USD ($)' },
                { value: 'EUR', label: 'EUR (€)' },
                { value: 'GBP', label: 'GBP (£)' },
              ]}
              ariaLabel="Currency selector"
              triggerClassName="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 rounded-md px-2.5 py-1.5 shadow-xs transition-colors cursor-pointer"
            />
            <CustomSelect
              value="en"
              onChange={() => undefined}
              options={[
                { value: 'en', label: 'English' },
                { value: 'zh', label: '中文' },
                { value: 'ja', label: '日本語' },
                { value: 'es', label: 'Español' },
              ]}
              ariaLabel="Language selector"
              icon={<Globe className="h-3.5 w-3.5 text-slate-500" />}
              triggerClassName="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 rounded-md px-2.5 py-1.5 shadow-xs transition-colors cursor-pointer"
            />
            <button
              type="button"
              aria-label="Favourites"
              className="inline-flex items-center justify-center p-1.5 text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 bg-white rounded-md transition-colors shadow-xs"
            >
              <Heart className="h-4 w-4" />
            </button>
            <Link
              to="/lookup"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-800 bg-white hover:bg-slate-50 border border-slate-300 rounded-md px-3.5 py-1.5 shadow-xs transition-colors"
            >
              <User className="h-3.5 w-3.5 text-slate-600" />
              Sign in
            </Link>
          </div>

          <button type="button" aria-label="Menu" className="p-2 md:hidden">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-background">
        <div className="mx-auto max-w-[1536px] px-6 lg:px-8 py-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground">Need help?</h4>
              <p className="mt-2 ibe-muted">Questions about your booking?</p>
              <p className="mt-1 text-sm text-primary">Contact Support</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground">Best Price Guarantee</h4>
              <p className="mt-2 ibe-muted">Found a lower price? We&apos;ll match it.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground">Secure Payments</h4>
              <p className="mt-2 ibe-muted">Trusted and safe checkout.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground">24/7 Support</h4>
              <p className="mt-2 ibe-muted">We&apos;re here to help anytime.</p>
            </div>
          </div>
          <div className="mt-8 border-t border-border pt-6 text-center">
            <p className="ibe-muted">
              &copy; {new Date().getFullYear().toString()} CLLIX. All rights reserved.
            </p>
            <div className="mt-2 flex justify-center gap-4 text-sm text-muted-foreground">
              <Link to="/lookup" className="hover:text-foreground">
                Manage your booking
              </Link>
              <Link to="/" className="hover:text-foreground">
                Terms & Conditions
              </Link>
              <Link to="/" className="hover:text-foreground">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HeaderNavLink({
  to,
  children,
}: {
  readonly to: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `text-sm font-medium hover:text-primary pb-0.5 ${
          isActive ? 'border-b-2 border-primary text-primary' : 'text-foreground'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function HeaderLink({
  to,
  children,
}: {
  readonly to: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Link to={to} className="text-sm font-medium text-foreground hover:text-primary pb-0.5">
      {children}
    </Link>
  );
}
